<template>
  <div class="params-tab">
    <div v-if="!selectedCategory" class="empty-state">
      <q-icon name="arrow_back" color="grey-5" size="48px" />
      <p class="empty-text">Выберите категорию слева</p>
    </div>

    <div v-else class="params-panel">
        <div class="params-header">
          <div class="header-left">
            <span class="header-title">Список параметров по категориям</span>
            <q-checkbox
              v-if="params.length"
              :model-value="allParamsSelected"
              @update:model-value="toggleAllParams"
              size="xs"
              dense
              class="header-checkbox"
            />
            <q-badge v-if="params.length" :label="filteredParams.length + '/' + params.length" color="grey-4" text-color="grey-8" />
          </div>
          <div class="header-right">
            <select
              v-model="paramFilterMode"
              @change="onParamFilterChange"
              class="filter-select"
            >
              <option value="all">Все</option>
              <option value="active">Только активные</option>
              <option value="inactive">Только неактивные</option>
            </select>
            <q-btn
              v-if="selectedParams.length > 0"
              icon="delete"
              flat
              dense
              round
              size="sm"
              color="negative"
              @click="confirmDeleteSelectedParams"
            >
              <q-tooltip>Удалить выбранные ({{ selectedParams.length }})</q-tooltip>
            </q-btn>
            <CustomButton
              color="primary-light"
              @click="openParamDialog()"
              style="width: auto; min-width: 120px; height: 36px; padding: 0 16px;"
            >
              Добавить
            </CustomButton>
          </div>
        </div>

        <div v-if="params.length" class="search-container">
          <q-input
            v-model="paramSearchQuery"
            dense
            outlined
            placeholder="Поиск параметров..."
            clearable
            class="search-input"
          >
            <template v-slot:prepend>
              <q-icon name="search" size="16px" />
            </template>
          </q-input>
        </div>

        <div class="params-list-container">
          <q-list v-if="!isLoadingParams && filteredParams.length > 0" dense class="params-list">
            <q-item
              v-for="param in filteredParams"
              :key="param.bim_category_param_id"
              class="param-item"
              dense
            >
              <q-item-section side style="min-width: 20px; padding-right: 4px;">
                <q-checkbox
                  :model-value="selectedParams.includes(param.bim_category_param_id)"
                  @update:model-value="(val) => toggleParamSelection(param.bim_category_param_id, val)"
                  @click.stop
                  size="xs"
                  dense
                />
              </q-item-section>
              <q-item-section side style="min-width: 40px; padding-right: 8px;">
                <q-toggle
                  :model-value="param.is_selected"
                  @update:model-value="(val) => toggleParamActive(param, val)"
                  color="positive"
                  size="sm"
                />
              </q-item-section>
              <q-item-section>
                <q-item-label class="param-label">{{ param.param_name }}</q-item-label>
                <q-item-label caption class="param-caption">
                  ID: {{ param.bim_param_id }}
                </q-item-label>
              <q-item-label caption class="param-caption" v-if="param.buffer_property_name">
                Свойство: {{ param.buffer_property_name }}
              </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>

          <div v-else-if="!isLoadingParams && filteredParams.length === 0" class="empty-state">
            <q-icon name="search_off" color="grey-5" size="48px" />
            <p class="empty-text">{{ paramSearchQuery ? 'Ничего не найдено' : 'Нет параметров' }}</p>
          </div>

          <div v-else class="loading-container">
            <q-spinner color="primary" size="30px" />
          </div>
        </div>
    </div>

    <!-- Диалог добавления/редактирования параметра -->
    <q-dialog v-model="paramDialog" persistent>
      <q-card class="bim-modal" style="min-width: 400px">
        <q-card-section class="bim-modal__header">
          <div class="text-h6">{{ editingParam ? 'Редактировать параметр' : 'Добавить параметр' }}</div>
        </q-card-section>

        <q-card-section class="bim-modal__body">
          <div class="bim-form">
            <div class="bim-form__item">
              <label class="bim-form__label">Параметр *</label>
              <CustomSelect
                :value="paramForm.bim_param_id"
                @change="(val) => paramForm.bim_param_id = val"
                :options="formattedParamOptions"
                option-label-key="param_name"
                option-value-key="bim_param_id"
                placeholder="Выберите параметр"
                :loading="isLoadingAllParams"
              />
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right" class="bim-modal__footer">
          <CustomButton
            flat
            @click="closeParamDialog"
          >
            Отмена
          </CustomButton>
          <CustomButton
            :loading="isSavingParam"
            @click="saveParam"
          >
            Сохранить
          </CustomButton>
        </q-card-actions>
      </q-card>
    </q-dialog>

    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomButton from "@/components/common/CustomButton.vue";
import CustomSelect from "@/components/form/CustomSelect";
import {
  getBimCategoryParams,
  getAllBimParams,
  saveBimCategoryParam,
  deleteBimCategoryParam
} from "@/views/BimCategoryParams/services";

export default {
  name: "ParamsTab",
  components: {
    CustomButton,
    CustomSelect
  },
  props: {
    selectedCategory: { type: Object, default: null },
    selectedCategoryId: { type: Number, default: null }
  },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar, showErrorSnackbar };
  },
  data() {
    return {
      params: [],
      allParams: [],
      isLoadingParams: false,
      isLoadingAllParams: false,
      isSavingParam: false,
      paramDialog: false,
      editingParam: null,
      paramForm: {
        bim_param_id: null
      },
      selectedParams: [],
      paramFilterMode: localStorage.getItem('bim_category_param_filter_mode') || 'all',
      paramSearchQuery: '',
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
    };
  },
  watch: {
    selectedCategoryId: {
      handler(id) {
        this.params = [];
        this.selectedParams = [];
        this.paramSearchQuery = '';
        if (id) this.loadParamsForCategory(id);
      },
      immediate: true
    }
  },
  computed: {
    allParamsSelected() {
      if (this.filteredParams.length === 0) return false;
      return this.filteredParams.every(param =>
        this.selectedParams.includes(param.bim_category_param_id)
      );
    },
    filteredParams() {
      let filtered = [...this.params];

      if (this.paramFilterMode === 'active') {
        filtered = filtered.filter(param => param.is_selected);
      } else if (this.paramFilterMode === 'inactive') {
        filtered = filtered.filter(param => !param.is_selected);
      }

      if (this.paramSearchQuery) {
        const query = this.paramSearchQuery.toLowerCase();
        filtered = filtered.filter(param =>
          param.param_name.toLowerCase().includes(query) ||
          param.bim_param_id.toString().includes(query) ||
          (param.buffer_property_name && param.buffer_property_name.toLowerCase().includes(query))
        );
      }

      return filtered;
    },
    formattedParamOptions() {
      const list = Array.isArray(this.allParams) ? this.allParams : [];
      return list.map(param => ({
        ...param,
        param_name: param.buffer_property_name
          ? `${param.param_name || ''} (${param.buffer_property_name})`
          : (param.param_name || '')
      }));
    }
  },
  mounted() {
    this.loadAllParams();
  },
  methods: {
    async loadAllParams() {
      this.isLoadingAllParams = true;
      try {
        const res = await getAllBimParams();
        this.allParams = Array.isArray(res) ? res : (res?.data != null ? res.data : []);
      } catch (e) {
        console.error('Error loading all params:', e);
        this.allParams = [];
      } finally {
        this.isLoadingAllParams = false;
      }
    },
    async loadParamsForCategory(bim_category_id) {
      this.isLoadingParams = true;
      try {
        const res = await getBimCategoryParams(bim_category_id);
        if (res) this.params = res;
      } catch (e) {
        console.error('Error loading params:', e);
      } finally {
        this.isLoadingParams = false;
      }
    },
    toggleAllParams(val) {
      if (val) {
        this.selectedParams = this.filteredParams.map(param => param.bim_category_param_id);
      } else {
        this.selectedParams = [];
      }
    },
    toggleParamSelection(paramId, val) {
      if (val) {
        if (!this.selectedParams.includes(paramId)) {
          this.selectedParams.push(paramId);
        }
      } else {
        this.selectedParams = this.selectedParams.filter(id => id !== paramId);
      }
    },
    onParamFilterChange() {
      localStorage.setItem('bim_category_param_filter_mode', this.paramFilterMode);
      this.selectedParams = [];
    },
    openParamDialog(param = null) {
      this.editingParam = param;
      if (param) {
        this.paramForm.bim_param_id = param.bim_param_id;
      } else {
        this.paramForm.bim_param_id = null;
      }
      this.paramDialog = true;
    },
    closeParamDialog() {
      this.paramDialog = false;
      this.editingParam = null;
      this.paramForm.bim_param_id = null;
    },
    async saveParam() {
      if (!this.paramForm.bim_param_id) {
        this.showErrorSnackbar('Выберите параметр');
        return;
      }

      this.isSavingParam = true;
      try {
        const data = {
          bim_category_param_id: this.editingParam?.bim_category_param_id || null,
          bim_category_id: this.selectedCategory.bim_category_id,
          bim_param_id: this.paramForm.bim_param_id,
          is_selected: true
        };

        await saveBimCategoryParam(data);
        this.showSucceedSnackbar('Параметр успешно сохранен');
        this.closeParamDialog();
        await this.loadParamsForCategory(this.selectedCategory.bim_category_id);
      } catch (e) {
        console.error('Error saving param:', e);
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка при сохранении параметра');
      } finally {
        this.isSavingParam = false;
      }
    },
    async toggleParamActive(param, val) {
      try {
        const data = {
          bim_category_param_id: param.bim_category_param_id,
          bim_category_id: param.bim_category_id,
          bim_param_id: param.bim_param_id,
          is_selected: val
        };

        await saveBimCategoryParam(data);
        param.is_selected = val;
      } catch (e) {
        console.error('Error toggling param:', e);
        this.showErrorSnackbar('Ошибка при изменении статуса параметра');
      }
    },
    confirmDeleteParam(param) {
      this.confirmDialog = {
        show: true,
        title: 'Удаление параметра',
        message: `Вы уверены, что хотите удалить параметр "${param.param_name}"?`,
        onConfirm: () => this.deleteParam(param.bim_category_param_id)
      };
    },
    confirmDeleteSelectedParams() {
      if (this.selectedParams.length === 0) return;

      this.confirmDialog = {
        show: true,
        title: 'Удаление параметров',
        message: `Вы уверены, что хотите удалить ${this.selectedParams.length} параметр(ов)?`,
        onConfirm: () => this.deleteSelectedParams()
      };
    },
    onConfirmDelete() {
      if (this.confirmDialog.onConfirm) {
        this.confirmDialog.onConfirm();
      }
      this.confirmDialog.show = false;
    },
    async deleteParam(bim_category_param_id) {
      try {
        await deleteBimCategoryParam(bim_category_param_id);
        this.showSucceedSnackbar('Параметр успешно удален');
        await this.loadParamsForCategory(this.selectedCategory.bim_category_id);
      } catch (e) {
        console.error('Error deleting param:', e);
        this.showErrorSnackbar(e?.response?.data?.message || 'Ошибка при удалении параметра');
      }
    },
    async deleteSelectedParams() {
      const paramsToDelete = [...this.selectedParams];
      this.selectedParams = [];

      for (const paramId of paramsToDelete) {
        try {
          await deleteBimCategoryParam(paramId);
        } catch (e) {
          console.error('Error deleting param:', e);
          this.showErrorSnackbar(`Ошибка при удалении параметра ${paramId}`);
        }
      }

      this.showSucceedSnackbar(`Удалено параметров: ${paramsToDelete.length}`);
      await this.loadParamsForCategory(this.selectedCategory.bim_category_id);
    }
  }
};
</script>

<style scoped>
.params-tab {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 400px;
}

.params-tab .empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
  text-align: center;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 60px 20px;
  color: #999;
  text-align: center;
}

.empty-text {
  margin-top: 16px;
  font-size: 16px;
}

.params-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.params-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: #fafafa;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  color: #333;
  cursor: pointer;
  min-width: 150px;
}

.filter-select:hover {
  border-color: #1976d2;
}

.filter-select:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.1);
}

.header-title {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.search-container {
  padding: 12px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.search-input {
  width: 100%;
}

.params-list-container {
  flex: 1;
  overflow-y: auto;
}

.params-list {
  padding: 8px 0;
}

.param-item {
  padding: 10px 20px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.param-item:hover {
  background: #f9f9f9;
}

.param-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.param-caption {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.bim-modal {
  min-width: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.bim-modal__header {
  padding: 24px 28px 20px;
  border-bottom: 1px solid #e0e0e0;
}

.bim-modal__body {
  padding: 24px 28px;
}

.bim-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 15px;
}

.bim-form__item {
  width: 100%;
}

.bim-form__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.bim-modal__footer {
  width: 100%;
  min-height: 46px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 20px 28px 24px;
  border-top: 1px solid #e0e0e0;
}
</style>
